import type {
  AdminLegalObligationConditionWriteResult,
  AdminLegalObligationIntervalVariantWriteResult,
  AdminLegalObligationListResponse,
  AdminLegalObligationRow,
  AdminLegalObligationTimelineResponse,
  AdminLegalObligationVerifyResult,
  ApiResponse,
  CreateAdminLegalObligationConditionBody,
  CreateAdminLegalObligationIntervalVariantBody,
  UpdateAdminLegalObligationBody,
  UpdateAdminLegalObligationConditionBody,
  UpdateAdminLegalObligationIntervalVariantBody,
} from '@safetyhub/shared';
import api from './api';

/**
 * **법정 의무 카탈로그** API 클라이언트 — 「법정 의무 카탈로그」 페이지(`/legal-obligation-catalog`)가 쓴다.
 *
 * base `/admin/legal-obligations`. 계약 정본은 shared `features/legal-obligation-catalog.ts`와 서버
 * `AdminLegalObligationsController` JSDoc이다. 권한은 `legal-obligation-catalog` 하나(읽기·쓰기 공용)다.
 *
 * ⚠️ 순수 판정(미기재 칸 · 저장 본문 만들기)은 이 파일에 두지 않는다 — 화면 spec이 이 모듈을
 * `vi.mock`하면 팩토리가 준 키만 남아 그 함수까지 사라진다. 그 자리는 `legalObligationCatalogFields.ts`·
 * `legalObligationConditionFields.ts`다.
 *
 * 쓰기 셋의 기록 축이 다르다(서버 카탈로그 서비스 docblock): 의무 PATCH · 조건·변형 줄의 규칙 값 = **개정**(이력 1행 · version +1) ·
 * 조건 근거 · 「대조 완료」 = **별 기록**(audit · version 불변). 이력 화면은 둘을 `timeline` 한 줄기로 읽는다(W3 2026-11-04 — 종전 `/history`에서 옮겼다).
 */

/** 봉투에서 본문을 꺼낸다 — 정본 타입이 `data: T | null`이라 그 null을 여기서 한 번 가른다(선례 `announcements.ts`) */
function unwrapEnvelope<T>(envelope: ApiResponse<T>): T {
  if (envelope.data === null) {
    throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
  }
  return envelope.data;
}

const BASE = '/admin/legal-obligations';

export const LEGAL_OBLIGATION_CATALOG_QUERY_KEYS = {
  list: ['admin', 'legal-obligations'] as const,
  timeline: (obligationId: string) => ['admin', 'legal-obligations', obligationId, 'timeline'] as const,
};

/** 개정 응답 — 서버 `UpdateLegalObligationResult` */
export interface UpdateLegalObligationResponse {
  /** 갱신된 카탈로그 행(날것 — 목록 행 형상과 다르다. 화면은 목록을 다시 읽는다) */
  obligation: { id: string; version: number };
  /** 실제로 달라진 칸 — **비면 아무 일도 하지 않았다**(같은 값을 보냈다 · version 그대로) */
  changedFields: string[];
  historyId: string | null;
}

export const legalObligationCatalogApi = {
  async list(): Promise<AdminLegalObligationRow[]> {
    const res = await api.get<ApiResponse<AdminLegalObligationListResponse>>(BASE);
    return unwrapEnvelope(res.data).obligations;
  },

  async update(
    obligationId: string,
    body: UpdateAdminLegalObligationBody,
  ): Promise<UpdateLegalObligationResponse> {
    const res = await api.patch<ApiResponse<UpdateLegalObligationResponse>>(
      `${BASE}/${obligationId}`,
      body,
    );
    return unwrapEnvelope(res.data);
  },

  /** 개정 이력 + 근거 변경 · 대조 완료 기록 — 시간순 한 줄기(최신 먼저) */
  async timeline(obligationId: string): Promise<AdminLegalObligationTimelineResponse> {
    const res = await api.get<ApiResponse<AdminLegalObligationTimelineResponse>>(
      `${BASE}/${obligationId}/timeline`,
    );
    return unwrapEnvelope(res.data);
  },

  /** 의무 단위 「조문과 대조 완료」 — 서버 시각 · 개정 아님 */
  async verifyObligation(obligationId: string): Promise<AdminLegalObligationVerifyResult> {
    const res = await api.post<ApiResponse<AdminLegalObligationVerifyResult>>(`${BASE}/${obligationId}/verify`, {});
    return unwrapEnvelope(res.data);
  },

  // ── 조건 줄 ──
  async createCondition(
    obligationId: string,
    body: CreateAdminLegalObligationConditionBody,
  ): Promise<AdminLegalObligationConditionWriteResult> {
    const res = await api.post<ApiResponse<AdminLegalObligationConditionWriteResult>>(
      `${BASE}/${obligationId}/conditions`,
      body,
    );
    return unwrapEnvelope(res.data);
  },

  async updateCondition(
    obligationId: string,
    conditionId: string,
    body: UpdateAdminLegalObligationConditionBody,
  ): Promise<AdminLegalObligationConditionWriteResult> {
    const res = await api.patch<ApiResponse<AdminLegalObligationConditionWriteResult>>(
      `${BASE}/${obligationId}/conditions/${conditionId}`,
      body,
    );
    return unwrapEnvelope(res.data);
  },

  async deleteCondition(obligationId: string, conditionId: string): Promise<AdminLegalObligationConditionWriteResult> {
    const res = await api.delete<ApiResponse<AdminLegalObligationConditionWriteResult>>(
      `${BASE}/${obligationId}/conditions/${conditionId}`,
    );
    return unwrapEnvelope(res.data);
  },

  /** 조건 단위 「별표와 대조 완료」 — 서버 시각 · 개정 아님 */
  async verifyCondition(obligationId: string, conditionId: string): Promise<AdminLegalObligationVerifyResult> {
    const res = await api.post<ApiResponse<AdminLegalObligationVerifyResult>>(
      `${BASE}/${obligationId}/conditions/${conditionId}/verify`,
      {},
    );
    return unwrapEnvelope(res.data);
  },

  // ── 변형 줄 ──
  async createVariant(
    obligationId: string,
    body: CreateAdminLegalObligationIntervalVariantBody,
  ): Promise<AdminLegalObligationIntervalVariantWriteResult> {
    const res = await api.post<ApiResponse<AdminLegalObligationIntervalVariantWriteResult>>(
      `${BASE}/${obligationId}/interval-variants`,
      body,
    );
    return unwrapEnvelope(res.data);
  },

  async updateVariant(
    obligationId: string,
    variantId: string,
    body: UpdateAdminLegalObligationIntervalVariantBody,
  ): Promise<AdminLegalObligationIntervalVariantWriteResult> {
    const res = await api.patch<ApiResponse<AdminLegalObligationIntervalVariantWriteResult>>(
      `${BASE}/${obligationId}/interval-variants/${variantId}`,
      body,
    );
    return unwrapEnvelope(res.data);
  },

  async deleteVariant(obligationId: string, variantId: string): Promise<AdminLegalObligationIntervalVariantWriteResult> {
    const res = await api.delete<ApiResponse<AdminLegalObligationIntervalVariantWriteResult>>(
      `${BASE}/${obligationId}/interval-variants/${variantId}`,
    );
    return unwrapEnvelope(res.data);
  },
};
